import React from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth, VIEW_MODES } from '../context/AuthContext';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { Badge } from './ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import AddTransactionDialog from './AddTransactionDialog';
import {
  LayoutDashboard,
  Receipt,
  Landmark,
  FileText,
  BarChart3,
  Settings,
  LogOut,
  Plus,
  Menu,
  X,
  Users,
  History,
  Wallet,
  Upload,
  Camera,
  Globe,
  ChevronLeft,
  ChevronRight,
  Eye,
  Sparkles,
  Calculator,
  UsersRound,
  Shield
} from 'lucide-react';

const Layout = () => {
  const { user, company, logout, viewMode, setViewMode, VIEW_MODES, isAdmin, canViewAudit, canEdit, isPlatformAdmin } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = React.useState(false);
  const [addDialogOpen, setAddDialogOpen] = React.useState(false);

  // Auto-collapse sidebar based on screen width
  React.useEffect(() => {
    const handleResize = () => {
      // Auto-collapse between lg (1024) and xl (1280)
      if (window.innerWidth >= 1024 && window.innerWidth < 1280) {
        setSidebarCollapsed(true);
      }
    };
    
    handleResize(); // Check on mount
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Listen for open-add-transaction event from dashboard
  React.useEffect(() => {
    const handleOpenAdd = () => setAddDialogOpen(true);
    window.addEventListener('open-add-transaction', handleOpenAdd);
    return () => window.removeEventListener('open-add-transaction', handleOpenAdd);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Navigation items with role-based visibility
  const allNavItems = [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', roles: ['admin', 'finance', 'accountant', 'viewer'] },
    { to: '/transactions', icon: Receipt, label: 'Transactions', roles: ['admin', 'finance', 'accountant', 'viewer'] },
    { to: '/receivables', icon: Users, label: 'Receivables', roles: ['admin', 'finance', 'accountant', 'viewer'] },
    { to: '/loans', icon: Landmark, label: 'Loans', roles: ['admin', 'finance', 'accountant', 'viewer'] },
    { to: '/taxes', icon: FileText, label: 'Taxes', roles: ['admin', 'finance', 'accountant'] },
    { to: '/reconciliation', icon: Wallet, label: 'Reconciliation', roles: ['admin', 'finance', 'accountant'] },
    { to: '/bank-import', icon: Upload, label: 'Bank Import', roles: ['admin', 'finance', 'accountant'] },
    { to: '/receipt-scanner', icon: Camera, label: 'Receipt Scanner', roles: ['admin', 'finance', 'accountant'] },
    { to: '/currency', icon: Globe, label: 'Currency', roles: ['admin', 'finance', 'accountant', 'viewer'] },
    { to: '/reports', icon: BarChart3, label: 'Reports', roles: ['admin', 'finance', 'accountant', 'viewer'] },
    { to: '/audit', icon: History, label: 'Audit Log', roles: ['admin', 'accountant'] },
    { to: '/team', icon: UsersRound, label: 'Team', roles: ['admin'] },
    { to: '/settings', icon: Settings, label: 'Settings', roles: ['admin', 'finance', 'accountant', 'viewer'] },
  ];

  // Platform admin nav item (only visible to platform admins)
  const platformAdminItem = { to: '/platform-admin', icon: Shield, label: 'Platform Admin', isPlatformAdminOnly: true };

  // Filter nav items based on user role
  const navItems = [
    ...allNavItems.filter(item => item.roles.includes(user?.role || 'viewer')),
    ...(isPlatformAdmin ? [platformAdminItem] : [])
  ];

  const sidebarWidth = sidebarCollapsed ? 'w-16' : 'w-56';
  const mainPadding = sidebarCollapsed ? 'lg:pl-16' : 'lg:pl-56';

  return (
    <TooltipProvider delayDuration={0}>
      <div className="min-h-screen bg-[hsl(var(--surface-2))]">
        {/* Mobile header */}
        <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b px-3 py-2.5 flex items-center justify-between">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 rounded-lg hover:bg-gray-100 active:bg-gray-200 transition-colors"
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {sidebarOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
          <h1 className="font-semibold text-base truncate max-w-[180px]">
            {company?.name || 'TavJournals'}
          </h1>
          {canEdit && (
            <Button
              size="sm"
              onClick={() => setAddDialogOpen(true)}
              className="bg-[hsl(var(--brand-500))] hover:bg-[hsl(var(--brand-600))] h-9 w-9 p-0"
              data-testid="mobile-add-button"
              aria-label="Add transaction"
            >
              <Plus size={18} />
            </Button>
          )}
        </div>

        {/* Sidebar */}
        <aside
          className={`fixed inset-y-0 left-0 z-40 ${sidebarWidth} bg-white/95 backdrop-blur-md border-r transition-all duration-200 ease-in-out ${
            sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
          }`}
        >
          <div className="flex flex-col h-full">
            {/* Logo */}
            <div className={`border-b flex items-center justify-between ${sidebarCollapsed ? 'p-3' : 'p-4'}`}>
              {!sidebarCollapsed && (
                <div className="min-w-0 flex-1">
                  <h1 className="text-lg font-bold text-[hsl(var(--brand-700))] truncate" style={{ fontFamily: 'Space Grotesk' }}>
                    TavJournals
                  </h1>
                  <p className="text-xs text-[hsl(var(--muted-ink))] truncate">{company?.name}</p>
                </div>
              )}
              {sidebarCollapsed && (
                <div className="w-full flex justify-center">
                  <div className="w-8 h-8 rounded-lg bg-[hsl(var(--brand-500))] flex items-center justify-center text-white font-bold text-sm">
                    TJ
                  </div>
                </div>
              )}
              {/* Collapse button - desktop only */}
              <button
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                className="hidden md:flex p-1.5 rounded-md hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
                aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              >
                {sidebarCollapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
              </button>
            </div>

            {/* Add Button - Hidden for viewers */}
            {canEdit && (
              <div className={sidebarCollapsed ? 'p-2' : 'p-3'}>
                {sidebarCollapsed ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        className="w-full bg-[hsl(var(--brand-500))] hover:bg-[hsl(var(--brand-600))] text-white h-10 p-0"
                        onClick={() => setAddDialogOpen(true)}
                        data-testid="add-transaction-open-button"
                      >
                        <Plus size={18} />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="right">Add Transaction</TooltipContent>
                  </Tooltip>
                ) : (
                  <Button
                    className="w-full bg-[hsl(var(--brand-500))] hover:bg-[hsl(var(--brand-600))] text-white text-sm h-9"
                    onClick={() => setAddDialogOpen(true)}
                    data-testid="add-transaction-open-button"
                  >
                    <Plus size={16} className="mr-1.5" />
                    Add Transaction
                  </Button>
                )}
              </div>
            )}

            {/* Navigation - Scrollable */}
            <ScrollArea className="flex-1">
              <nav className={`py-1 ${sidebarCollapsed ? 'px-2' : 'px-2'}`}>
                {navItems.map((item) => (
                  sidebarCollapsed ? (
                    <Tooltip key={item.to}>
                      <TooltipTrigger asChild>
                        <NavLink
                          to={item.to}
                          onClick={() => setSidebarOpen(false)}
                          className={({ isActive }) =>
                            `flex items-center justify-center w-full h-10 rounded-lg transition-colors mb-1 ${
                              isActive
                                ? 'bg-[hsl(var(--brand-50))] text-[hsl(var(--brand-700))]'
                                : 'text-[hsl(var(--ink-2))] hover:bg-[hsl(var(--surface-2))]'
                            }`
                          }
                          data-testid={`nav-${item.label.toLowerCase()}`}
                        >
                          <item.icon size={18} />
                        </NavLink>
                      </TooltipTrigger>
                      <TooltipContent side="right">{item.label}</TooltipContent>
                    </Tooltip>
                  ) : (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      onClick={() => setSidebarOpen(false)}
                      className={({ isActive }) =>
                        `flex items-center gap-2.5 px-3 py-2 rounded-lg transition-colors text-sm mb-0.5 ${
                          isActive
                            ? 'bg-[hsl(var(--brand-50))] text-[hsl(var(--brand-700))] font-medium'
                            : 'text-[hsl(var(--ink-2))] hover:bg-[hsl(var(--surface-2))]'
                        }`
                      }
                      data-testid={`nav-${item.label.toLowerCase()}`}
                    >
                      <item.icon size={18} />
                      <span className="truncate">{item.label}</span>
                    </NavLink>
                  )
                ))}
              </nav>
            </ScrollArea>

            {/* View Mode Selector */}
            <div className={`border-t ${sidebarCollapsed ? 'p-2' : 'p-3'}`}>
              {sidebarCollapsed ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={() => {
                        const modes = [VIEW_MODES.SIMPLE, VIEW_MODES.STANDARD, VIEW_MODES.DETAILED];
                        const currentIndex = modes.indexOf(viewMode);
                        const nextIndex = (currentIndex + 1) % modes.length;
                        setViewMode(modes[nextIndex]);
                      }}
                      className="w-full flex justify-center p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                      data-testid="view-mode-toggle"
                    >
                      {viewMode === VIEW_MODES.SIMPLE ? <Eye size={18} /> :
                       viewMode === VIEW_MODES.DETAILED ? <Calculator size={18} /> :
                       <Sparkles size={18} />}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="right">
                    View: {viewMode === VIEW_MODES.SIMPLE ? 'Simple (CEO)' :
                           viewMode === VIEW_MODES.DETAILED ? 'Detailed (Accountant)' :
                           'Standard (Finance)'}
                  </TooltipContent>
                </Tooltip>
              ) : (
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-[hsl(var(--muted-ink))]">Dashboard View</label>
                  <Select value={viewMode} onValueChange={setViewMode}>
                    <SelectTrigger className="h-8 text-xs" data-testid="view-mode-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={VIEW_MODES.SIMPLE}>
                        <div className="flex items-center gap-2">
                          <Eye size={14} />
                          <span>Simple (CEO)</span>
                        </div>
                      </SelectItem>
                      <SelectItem value={VIEW_MODES.STANDARD}>
                        <div className="flex items-center gap-2">
                          <Sparkles size={14} />
                          <span>Standard (Finance)</span>
                        </div>
                      </SelectItem>
                      <SelectItem value={VIEW_MODES.DETAILED}>
                        <div className="flex items-center gap-2">
                          <Calculator size={14} />
                          <span>Detailed (Accountant)</span>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {/* User section */}
            <div className={`border-t ${sidebarCollapsed ? 'p-2' : 'p-3'}`}>
              {sidebarCollapsed ? (
                <>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="w-full flex justify-center mb-2">
                        <div className="w-9 h-9 rounded-full bg-[hsl(var(--brand-100))] flex items-center justify-center text-[hsl(var(--brand-700))] font-medium text-sm cursor-default">
                          {user?.name?.charAt(0) || '?'}
                        </div>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="right">
                      <p className="font-medium">{user?.name}</p>
                      <p className="text-xs text-muted-foreground">{user?.email}</p>
                      <p className="text-xs mt-1 capitalize">
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
                          user?.role === 'admin' ? 'bg-purple-100 text-purple-700' :
                          user?.role === 'finance' ? 'bg-blue-100 text-blue-700' :
                          user?.role === 'accountant' ? 'bg-green-100 text-green-700' :
                          'bg-amber-100 text-amber-700'
                        }`}>
                          {user?.role || 'user'}
                        </span>
                      </p>
                    </TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full h-9 p-0"
                        onClick={handleLogout}
                        data-testid="logout-button"
                      >
                        <LogOut size={16} />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="right">Logout</TooltipContent>
                  </Tooltip>
                </>
              ) : (
                <>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 rounded-full bg-[hsl(var(--brand-100))] flex items-center justify-center text-[hsl(var(--brand-700))] font-medium text-sm shrink-0">
                      {user?.name?.charAt(0) || '?'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <p className="font-medium text-sm truncate">{user?.name}</p>
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium uppercase tracking-wide ${
                          user?.role === 'admin' ? 'bg-purple-100 text-purple-700' :
                          user?.role === 'finance' ? 'bg-blue-100 text-blue-700' :
                          user?.role === 'accountant' ? 'bg-green-100 text-green-700' :
                          'bg-amber-100 text-amber-700'
                        }`} data-testid="user-role-badge">
                          {user?.role || 'user'}
                        </span>
                      </div>
                      <p className="text-xs text-[hsl(var(--muted-ink))] truncate">{user?.email}</p>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full h-8 text-xs"
                    onClick={handleLogout}
                    data-testid="logout-button"
                  >
                    <LogOut size={14} className="mr-1.5" />
                    Logout
                  </Button>
                </>
              )}
            </div>
          </div>
        </aside>

        {/* Overlay for mobile */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-30 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main content */}
        <main className={`${mainPadding} pt-14 lg:pt-0 min-h-screen transition-all duration-200`}>
          <div className="p-3 sm:p-4 md:p-6 lg:p-8 max-w-[1600px]">
            <Outlet />
          </div>
        </main>

        {/* Add Transaction Dialog */}
        <AddTransactionDialog
          open={addDialogOpen}
          onOpenChange={setAddDialogOpen}
        />
      </div>
    </TooltipProvider>
  );
};

export default Layout;
